import * as React from 'react';
import {Component} from 'react';
import {observer} from 'mobx-react';
import classNames from 'classnames';
import {selectField, activateField, addToSelectedGroup} from '../actions/Screen';
import {changeTitle} from '../actions/Field';
import {cancelTitleChange} from '../../Common/actions/Field';
import * as fieldTypes from '../../../../constants/FieldTypes';
import {fieldMaxLength, hiddenFieldTypes} from '../../../../constants/FieldConsts';
import {textConst, getLocalizedText, texts} from '../../../../constants/TextConst';
import {withElementRef, InjectedElementRefProps} from '../../../components/Fields/withElementRef';
import {withScrollOnMove} from '../../../components/Fields/withScrollOnMove';
import {InjectedCollapsibleFieldProps} from '../../../../components/Common/Collapsible/Field';
import {InjectedParentFieldProps} from '../../../../components/Common/Collapsible/Collection';
import TooltipedImg from '../../../../components/Common/TooltipedImg';
import ToggleEditTitle from '../../../components/Fields/ToggleEditTitle';
import ExpandCollapseIcon from '../../../../components/Common/Collapsible/ExpandCollpaseIcon';
import {isMacOS} from '../../../../utils/platform';

import packageHidden from '../../../../images/package_hidden.png';
import hidden from '../../../../images/hidden.png';
import newarea from '../../../../images/newarea.png';
import merge from '../../../../images/merge.png';
import more from '../../../../images/more.png';
import mergePassive from '../../../../images/merge-passive.png';
import pin from '../../../../images/pin.png';

interface Props {
    field: any;
}

@observer
class Design extends Component<
    Props & InjectedElementRefProps & InjectedCollapsibleFieldProps & InjectedParentFieldProps,
    null
> {
    constructor(props) {
        super(props);
        this.activateField = this.activateField.bind(this);
        this.selectField = this.selectField.bind(this);
        this.changeTitle = this.changeTitle.bind(this);
        this.cancelTitleChange = this.cancelTitleChange.bind(this);
    }

    activateField(e) {
        e.stopPropagation();
        const {field} = this.props;
        activateField(field);
    }

    selectField(e) {
        const {field, isParent, toggleExpanded} = this.props;
        const isMac = isMacOS();
        if ((isMac && e.metaKey) || (!isMac && e.ctrlKey)) {
            addToSelectedGroup(field);
        } else {
            selectField(field);
        }

        if (isParent) {
            toggleExpanded();
        }
    }

    changeTitle(title, callback) {
        const {field} = this.props;
        changeTitle(field, title, callback);
    }

    cancelTitleChange() {
        const {field} = this.props;
        cancelTitleChange(field);
    }

    renderRightHandImages() {
        const {field, elementRef} = this.props;

        return (
            <div className="righthand-imgs-wrapper">
                {field.newarea && (
                    <TooltipedImg
                        componentClasses="righthand-img"
                        src={newarea}
                        alt={textConst.newAreaAlt}
                        tooltipText={getLocalizedText(texts.newBoxFieldTooltip)}
                        height={50}
                        position="left"
                        containerRef={elementRef}
                    />
                )}
                {field.merge && (
                    <TooltipedImg
                        componentClasses="righthand-img"
                        src={merge}
                        alt={textConst.mergedFieldAlt}
                        tooltipText={getLocalizedText(texts.mergeTooltip)}
                        height={50}
                        position="left"
                        containerRef={elementRef}
                    />
                )}
                {field.merged && (
                    <TooltipedImg
                        componentClasses="righthand-img merged-img"
                        src={mergePassive}
                        alt={textConst.mergedFieldAlt}
                        tooltipText={getLocalizedText(texts.mergeTooltip)}
                        height={50}
                        position="left"
                        containerRef={elementRef}
                    />
                )}
                {field.cover && (
                    <img
                        className="righthand-img pin-img"
                        alt={textConst.pinnedFieldAlt}
                        src={pin}
                    />
                )}
            </div>
        );
    }

    renderMoreImage() {
        const {field} = this.props;

        return field.grouped || field.general ? null : (
            <img className="more-sign" alt="Show more" src={more} onClick={this.activateField} />
        );
    }

    renderHiddenImage() {
        const {field, elementRef} = this.props;
        const {screen} = field;

        const fieldHiddenImg =
            field.hidden === hiddenFieldTypes.PACKAGE_HIDDEN ? packageHidden : hidden;
        const fieldHiddenTooltip =
            field.hidden === hiddenFieldTypes.PACKAGE_HIDDEN
                ? getLocalizedText(texts.hiddenPack)
                : screen.hiddenEntityName;

        return field.hidden ? (
            <TooltipedImg
                componentClasses="hidden-package-image"
                src={fieldHiddenImg}
                alt={textConst.hiddenSignAlt}
                tooltipText={fieldHiddenTooltip}
                height={50}
                containerRef={elementRef}
                position="right"
            />
        ) : null;
    }

    render() {
        const {field, depth, isParent, isExpanded} = this.props;
        const {hidden, newarea, merge, merged, isChanging} = field;

        const fieldClass = classNames('field', {
            tab: field.type === fieldTypes.TAB,
            expanded: isExpanded,
            selected: field.isSelected || field.grouped,
            active: field.isActive && !isChanging,
            hidden: hidden,
            newarea: newarea,
            merge: merge,
            merged: merged
        });

        const depthStyle = {
            marginRight: `${depth * 30}px`
        };

        return (
            <div className={fieldClass} onClick={this.selectField} ref={this.props.elementRef}>
                <div style={depthStyle} />
                {isParent && <ExpandCollapseIcon isExpanded={this.props.isExpanded} />}
                <ToggleEditTitle
                    isChanging={field.isChanging}
                    title={field.title}
                    maxLength={fieldMaxLength}
                    onApprove={this.changeTitle}
                    handleClose={this.cancelTitleChange}
                    saveOnBlur
                >
                    {this.renderRightHandImages()}
                    <span title={field.title}>{field.title}</span>
                    <div className="images">
                        {this.renderHiddenImage()}
                        {this.renderMoreImage()}
                    </div>
                </ToggleEditTitle>
            </div>
        );
    }
}

const DesignFieldComponent = withElementRef(withScrollOnMove(Design));

const DesignField = observer(({field, depth = 0, ...props}) => {
    return (
        <div>
            <DesignFieldComponent {...props} field={field} depth={depth} />
            {field.merge && field.mergedField && (
                <DesignFieldComponent {...props} field={field.mergedField} depth={depth + 1} />
            )}
        </div>
    );
});

export default DesignField;
